import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useCreateTasksForTeamMutation, useGetTaskPrioritiesQuery } from '../../api/gridApi';
import type { CreateTaskForTeamInput } from '../../types';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import {
  normalizeLegacyEstimateTime,
  validateCreateTaskForTeamInput,
} from './createTaskForTeamUtils';

type CreateTaskForTeamDialogProps = {
  open: boolean;
  title?: string | null;
  projectId: number;
  nodeKey: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
};

export const CreateTaskForTeamDialog = ({
  open,
  title,
  projectId,
  nodeKey,
  onClose,
  onSuccess,
}: CreateTaskForTeamDialogProps) => {
  const [taskName, setTaskName] = useState('');
  const [priorityId, setPriorityId] = useState<number | null>(null);
  const [estimate, setEstimate] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const prioritiesQuery = useGetTaskPrioritiesQuery(undefined, { skip: !open });
  const [createTasks, createState] = useCreateTasksForTeamMutation();
  const priorities = prioritiesQuery.data ?? [];
  const hasPriorities = prioritiesQuery.isSuccess && priorities.length > 0;
  const prioritiesError = prioritiesQuery.error
    ? extractErrorMessage(prioritiesQuery.error, 'Failed to load task priorities.')
    : !prioritiesQuery.isLoading && prioritiesQuery.isSuccess && priorities.length === 0
      ? 'No priorities found.'
      : null;

  const errorMessage = prioritiesError
    ? prioritiesError
    : createState.error
      ? extractErrorMessage(createState.error, 'Failed to create tasks for team members.')
      : validationMessage;

  const resetForm = () => {
    setTaskName('');
    setPriorityId(null);
    setEstimate(null);
    setStartDate('');
    setEndDate('');
    setValidationMessage(null);
    createState.reset();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleApply = async () => {
    const validationError = validateCreateTaskForTeamInput({
      hasPriorities,
      taskName,
      estimate,
      startDate,
      endDate,
    });
    if (validationError) {
      setValidationMessage(validationError);
      return;
    }

    setValidationMessage(null);

    const input: CreateTaskForTeamInput = {
      taskName: taskName.trim(),
      projectId,
      priorityId,
      taskEstimateTimeInRelease: normalizeLegacyEstimateTime(estimate),
      taskStart: startDate,
      taskEnd: endDate || null,
    };

    try {
      await createTasks({ projectId, nodeKey, input }).unwrap();
      resetForm();
      onSuccess('Tasks created for all team members.');
    } catch {
      // Error is exposed via createState.error and rendered in the dialog.
    }
  };

  const isLoading = prioritiesQuery.isLoading || createState.isLoading;
  const isCreateDisabled = isLoading || !hasPriorities;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{title || 'Create Task for All Team Members'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {prioritiesQuery.isLoading && (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={24} />
            </Stack>
          )}

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <TextField
            required
            label="Task Name"
            value={taskName}
            onChange={(event) => {
              setValidationMessage(null);
              setTaskName(event.target.value);
            }}
            multiline
            minRows={3}
            disabled={isLoading}
          />

          <FormControl fullWidth disabled={isLoading}>
            <InputLabel id="task-priority-label">Priority</InputLabel>
            <Select
              labelId="task-priority-label"
              label="Priority"
              value={priorityId ?? ''}
              onChange={(event) => {
                setValidationMessage(null);
                setPriorityId(event.target.value ? Number(event.target.value) : null);
              }}
            >
              <MenuItem value="">None</MenuItem>
              {priorities.map((priority) => (
                <MenuItem key={priority.priorityId} value={priority.priorityId}>
                  {priority.priorityName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Estimate Time"
            type="number"
            value={estimate ?? ''}
            onChange={(event) => {
              setValidationMessage(null);
              setEstimate(event.target.value === '' ? null : Number(event.target.value));
            }}
            inputProps={{ min: 0, step: 'any' }}
            disabled={isLoading}
          />

          <TextField
            required
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(event) => {
              setValidationMessage(null);
              setStartDate(event.target.value);
            }}
            InputLabelProps={{ shrink: true }}
            disabled={isLoading}
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(event) => {
              setValidationMessage(null);
              setEndDate(event.target.value);
            }}
            InputLabelProps={{ shrink: true }}
            disabled={isLoading}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createState.isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} disabled={isCreateDisabled}>
          {createState.isLoading ? <CircularProgress size={20} /> : 'Create Tasks'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTaskForTeamDialog;
